import { CustomFields, Entity, Location } from '@nx-smartmonkey/shared/domain';
import { removeEmpty } from '@nx-smartmonkey/shared/helpers';
import { ClientMapper } from '../../mappers/client/ClientMapper';
import { Stop } from '../stop/Stop';

export interface ClientUpdateProps {
  label?: string;
  external_id?: string;
  location?: Location | null;
  location_details?: string;
  comments?: string;
  phone?: string;
  email?: string;
  url?: string;
  reference_person?: string;
  default_duration?: number;
  default_requires?: string[];
  default_time_windows?: Array<[number, number]>;
  default_volume?: number;
  default_weight?: number;
  custom_fields?: CustomFields;
  default_max_delivery_time?: number;
  default_cluster?: string;
}

export interface ClientProps {
  organization_id?: string;
  project_id: string;
  label?: string;
  external_id?: string;
  location?: Location | null;
  location_details?: string;
  comments?: string;
  phone?: string;
  email?: string;
  url?: string;
  reference_person?: string;
  default_duration?: number;
  default_requires?: string[];
  default_time_windows?: Array<[number, number]>;
  default_volume?: number;
  default_weight?: number;
  custom_fields?: CustomFields;
  default_max_delivery_time?: number;
  default_cluster?: string;
  deleted?: boolean;
  created_by?: string;
  created_at?: Date;
  updated_at?: Date;
  deleted_at?: Date;
}

export class Client extends Entity<ClientProps> {
  get id(): string {
    return this._id!;
  }
  get organization_id(): string | undefined {
    return this.props.organization_id;
  }
  set organization_id(organizationId: string | undefined) {
    this.props.organization_id = organizationId;
  }
  get project_id(): string {
    return this.props.project_id;
  }
  set project_id(projectId: string) {
    this.props.project_id = projectId;
  }
  get label(): string | undefined {
    return this.props.label;
  }
  set label(label: string | undefined) {
    this.props.label = label;
  }
  get external_id(): string | undefined {
    return this.props.external_id;
  }
  set external_id(external_id: string | undefined) {
    this.props.external_id = external_id;
  }
  get location(): Location | undefined | null {
    return this.props.location;
  }
  set location(location: Location | undefined | null) {
    this.props.location = location;
  }
  get location_details(): string | undefined {
    return this.props.location_details;
  }
  set location_details(location_details: string | undefined) {
    this.props.location_details = location_details;
  }
  get comments(): string | undefined {
    return this.props.comments;
  }
  set comments(comments: string | undefined) {
    this.props.comments = comments;
  }
  get phone(): string | undefined {
    return this.props.phone;
  }
  set phone(phone: string | undefined) {
    this.props.phone = phone;
  }
  get email(): string | undefined {
    return this.props.email;
  }
  set email(email: string | undefined) {
    this.props.email = email;
  }
  get url(): string | undefined {
    return this.props.url;
  }
  set url(url: string | undefined) {
    this.props.url = url;
  }
  get reference_person(): string | undefined {
    return this.props.reference_person;
  }
  set reference_person(reference_person: string | undefined) {
    this.props.reference_person = reference_person;
  }
  get default_duration(): number | undefined {
    return this.props.default_duration;
  }
  set default_duration(default_duration: number | undefined) {
    this.props.default_duration = default_duration;
  }
  get default_requires(): string[] | undefined {
    return this.props.default_requires;
  }
  set default_requires(default_requires: string[] | undefined) {
    this.props.default_requires = default_requires;
  }
  get default_time_windows(): Array<[number, number]> | undefined {
    return this.props.default_time_windows;
  }
  set default_time_windows(default_time_windows: Array<[number, number]> | undefined) {
    this.props.default_time_windows = default_time_windows;
  }
  get default_volume(): number | undefined {
    return this.props.default_volume;
  }
  set default_volume(default_volume: number | undefined) {
    this.props.default_volume = default_volume;
  }
  get default_weight(): number | undefined {
    return this.props.default_weight;
  }
  set default_weight(default_weight: number | undefined) {
    this.props.default_weight = default_weight;
  }
  get custom_fields(): CustomFields | undefined {
    return this.props.custom_fields;
  }
  set custom_fields(custom_fields: CustomFields | undefined) {
    this.props.custom_fields = custom_fields;
  }
  get default_max_delivery_time(): number | undefined {
    return this.props.default_max_delivery_time;
  }
  set default_max_delivery_time(default_max_delivery_time: number | undefined) {
    this.props.default_max_delivery_time = default_max_delivery_time;
  }
  get default_cluster(): string | undefined {
    return this.props.default_cluster;
  }
  set default_cluster(default_cluster: string | undefined) {
    this.props.default_cluster = default_cluster;
  }
  get deleted(): boolean | undefined {
    return this.props.deleted;
  }
  get created_by(): string | undefined {
    return this.props.created_by;
  }
  set created_by(created_by: string | undefined) {
    this.props.created_by = created_by;
  }
  get created_at(): Date | undefined {
    return this.props.created_at;
  }
  set created_at(created_at: Date | undefined) {
    this.props.created_at = created_at;
  }
  get updated_at(): Date | undefined {
    return this.props.updated_at;
  }
  set updated_at(updated_at: Date | undefined) {
    this.props.updated_at = updated_at;
  }
  get deleted_at(): Date | undefined {
    return this.props.deleted_at;
  }
  set deleted_at(deleted_at: Date | undefined) {
    this.props.deleted_at = deleted_at;
  }
  update(props: ClientUpdateProps) {
    removeEmpty(props);

    if (props.label !== undefined) this.props.label = props.label;
    if (props.external_id !== undefined) this.props.external_id = props.external_id;
    if (props.location !== undefined) this.props.location = props.location;
    if (props.location_details !== undefined) this.props.location_details = props.location_details;
    if (props.comments !== undefined) this.props.comments = props.comments;
    if (props.phone !== undefined) this.props.phone = props.phone;
    if (props.email !== undefined) this.props.email = props.email;
    if (props.url !== undefined) this.props.url = props.url;
    if (props.reference_person !== undefined) this.props.reference_person = props.reference_person;
    if (props.default_duration !== undefined) this.props.default_duration = props.default_duration;
    if (props.default_requires !== undefined) this.props.default_requires = props.default_requires;
    if (props.default_time_windows !== undefined) this.props.default_time_windows = props.default_time_windows;
    if (props.default_volume !== undefined) this.props.default_volume = props.default_volume;
    if (props.default_weight !== undefined) this.props.default_weight = props.default_weight;
    if (props.custom_fields !== undefined) this.props.custom_fields = props.custom_fields;
    if (props.default_max_delivery_time !== undefined)
      this.props.default_max_delivery_time = props.default_max_delivery_time;
    if (props.default_cluster !== undefined) this.props.default_cluster = props.default_cluster;

    this.props.updated_at = new Date();
  }
  private constructor(props: ClientProps, id?: string) {
    super(props, id);
  }
  static create(props: ClientProps, id?: string): Client {
    return new Client(props, id);
  }

  stopFromClient(): Stop {
    return ClientMapper.toStop(this);
  }
}
